import { z } from 'zod';

/** Serialized template-owned text fields; no general form/execution language. */
export const documentInputFieldSchema = z.object({
  key: z.string(), label: z.string(), type: z.literal('text'),
  required: z.boolean(), maxLength: z.number().int().positive(), maxLines: z.number().int().positive(),
});
export type DocumentInputField = z.infer<typeof documentInputFieldSchema>;
export type DocumentInputs = Readonly<Record<string, string>>;
export const documentInputsSchema = z.record(z.string());
export const ceremonyInputDescriptionSchema = z.object({
  templateId: z.string(), documentInputs: z.array(documentInputFieldSchema),
});
export type CeremonyInputDescription = z.infer<typeof ceremonyInputDescriptionSchema>;

/** Same normalization and validation in the form and the creation API. */
export function validateDocumentInputs(fields: readonly DocumentInputField[], input: DocumentInputs = {}):
  | { success: true; values: Record<string, string> }
  | { success: false; message: string } {
  const accepted = new Set(fields.map(field => field.key));
  if (Object.keys(input).some(key => !accepted.has(key))) {
    return { success: false, message: 'This template does not accept one or more document fields.' };
  }
  const entries: [string, string][] = [];
  for (const field of fields) {
    const value = (input[field.key] ?? '').replace(/\r\n?/g, '\n').trim();
    if (!value) {
      if (field.required) return { success: false, message: `${field.label} is required.` };
      continue;
    }
    if (/[\n\u2028\u2029]/u.test(value)) {
      return { success: false, message: `${field.label} must be a single line.` };
    }
    if (value.length > field.maxLength) {
      return { success: false, message: `${field.label} must be at most ${field.maxLength} characters.` };
    }
    if (/[\u0000-\u0009\u000b\u000c\u000e-\u001f\u007f]/u.test(value)) {
      return { success: false, message: `${field.label} contains unsupported control characters.` };
    }
    entries.push([field.key, value]);
  }
  return { success: true, values: Object.fromEntries(entries) };
}
